import React, { useState } from "react";
import { EuiFlexGroup, EuiFlexItem, EuiText, EuiToolTip } from "@elastic/eui";
import { COLORS } from "Consts";
import IconButton from "components/Buttons/IconButton";
import Popover from "components/Popover/Popover";
import { exportFDDataTable } from "utils";
import { FDTableColumn } from "types/app";
import ExportIcon from "components/Icons/ExportIcon";

interface ExportConfig {
  sheets: Array<{
    columns: Array<FDTableColumn>;
    data: any[];
    sheetName: string;
  }>;
  fileName: string;
}

interface ActionButtonProps {
  type?: string;
  disabled?: boolean;
  loading?: boolean;
  invert?: boolean;
  onClick?: (e: any) => any;
  counter?: number | string | null;
  color?: string;
  icon?: any;
  style?: any;
  size?: any;
  iconSize?: "m" | "s" | "original" | "l" | "xl" | "xxl";
  text?: string | null | undefined;
  // Action button specific props
  exportConfig?: ExportConfig;
  popoverMessage?: string;
  tooltip?: string;
}

interface ActionButtonsProps {
  buttons: ActionButtonProps[];
  length?: number | undefined;
  currentUser?: any;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ buttons, length, currentUser }) => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const placeHolderList =
    length && buttons?.length < length ? Array(length - buttons?.length).fill(0) : [];

  const handleExport = (config: ExportConfig) => {
    if (!config || !currentUser) {
      console.error("Export configuration or current user is missing.");
      return;
    }
    exportFDDataTable(config.sheets, currentUser, config.fileName);
  };

  const renderButtonWithTooltip = (buttonElement: React.ReactElement, tooltip?: string) => {
    if (tooltip) {
      return <EuiToolTip content={tooltip}>{buttonElement}</EuiToolTip>;
    }
    return buttonElement;
  };

  return (
    <EuiFlexItem grow={false}>
      <EuiFlexGroup
        responsive={false}
        style={{ width: "fit-content" }}
        alignItems="center"
        gutterSize="s"
        justifyContent="center">
        {placeHolderList?.length > 0 &&
          placeHolderList.map((_, idx) => (
            <EuiFlexItem
              grow={false}
              style={{ width: "40px", height: "40px" }}
              key={`placeholder_btn_${idx}`}
            />
          ))}
        {buttons?.map((btn, idx) => (
          <EuiFlexItem grow={false} style={btn.style} key={`icon_btn_${idx}`}>
            {btn?.type === "info" && (
              <Popover
                button={
                  <IconButton
                    color={btn?.color || COLORS.$euiColorMediumShade}
                    type={"info"}
                    iconSize="l"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(!isOpen);
                    }}
                  />
                }
                onClose={() => setOpen(false)}
                isOpen={isOpen}
                ownFocus={false}
                children={<EuiText>{btn?.popoverMessage || ""}</EuiText>}
              />
            )}
            {btn?.type === "export" &&
              btn.exportConfig &&
              renderButtonWithTooltip(
                <IconButton
                  type={"download"}
                  onClick={() => handleExport(btn.exportConfig!)}
                  disabled={btn?.disabled || false}
                  color={btn?.color || COLORS.$blue}
                  loading={btn?.loading || false}
                />,
                btn.tooltip
              )}
            {btn?.type !== "info" &&
              btn?.type !== "export" &&
              btn?.onClick &&
              renderButtonWithTooltip(
                <IconButton
                  type={btn?.type}
                  onClick={btn.onClick}
                  icon={btn?.icon || null}
                  disabled={btn?.disabled || false}
                  counter={btn?.counter || null}
                  color={btn?.color || COLORS.$blue}
                  loading={btn?.loading || false}
                  invert={btn?.invert || false}
                />,
                btn.tooltip
              )}
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>
    </EuiFlexItem>
  );
};

export default ActionButtons;
